/**
 * The assistant (0.9.5): a floating **Ask** button (keyboard `?`) that opens a side panel — a question box,
 * the answer in the person's language (a live region), "Open the screen" links, "Was this helpful?", and
 * the reminder that it explains but cannot act. Esc closes and focus returns to the button; after an
 * answer, focus returns to the question box. Hidden for the crew, when the admin switched it off, when the
 * AOAI settings are missing, and for anyone who hid it under Settings.
 * 1.5.17: the panel is a conversation (my question, "Thinking…", the answer) and, on a phone, a bottom sheet
 * that rises above the keyboard — see AskPanel.
 */
import { useCallback, useEffect, useRef, useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import type { AssistantAnswer, AssistantExplainRequest, AssistantThread } from '@shared/assistant';
import { ASSISTANT_MAX_QUESTION } from '@shared/assistant';
import { hasAnyRole } from '@shared/roles';
import { ApiRequestError } from '@/lib/api';
import { ASSISTANT_ROLES, OPEN_ASK_EVENT, askAssistant, explainWithAssistant, useAssistantStatus, type AskExtra } from '@/lib/assistant';
import { MarkdownText } from '@/lib/docs';
import { runShowMe } from '@/lib/showme';
import { usePrefs } from '@/lib/prefs';
import { useScrollLock } from '@/lib/useScrollLock';
import { useVisualViewport } from '@/lib/useVisualViewport';
import type { Me } from '@/auth/useMe';
import { Icon } from './Icons';
import { Button } from './ui/button';
import { Sheet, SheetContent } from './ui/sheet';
import { useAnnounce } from './Announcer';
import { ChipsView, KindView, VersionAndThumbs, type Press } from './AssistantTurn';

export function useAssistantVisible(me: Me): boolean {
  const { prefs } = usePrefs();
  const allowed = hasAnyRole(me.roles, ASSISTANT_ROLES) && me.role !== 'crew';
  const status = useAssistantStatus(allowed && !prefs.hideAssistant);
  return allowed && !prefs.hideAssistant && status.data?.state === 'on';
}

/** The answer block: the words, the links, the sources, and the helpful thumbs. Shared with Explain. */
export function AnswerView({ answer, onNavigate, testId = 'assistant-answer', question = '' }: { answer: AssistantAnswer; onNavigate?: () => void; testId?: string; question?: string }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const announce = useAnnounce();
  return (
    <div className="assistant-answer" data-testid={testId}>
      <div className="assistant-words">
        <MarkdownText text={answer.answer} headingLevel={3} />
      </div>
      {answer.showMe ? (
        <div className="chips">
          <Button
            size="sm"
            aria-label={t('assistant.showMeOn', { screen: answer.showMe.label })}
            data-testid="assistant-show-me"
            onClick={() => {
              const target = answer.showMe!;
              onNavigate?.();
              runShowMe(target, navigate, t('assistant.here'), (found) => announce(found ? t('assistant.shown', { screen: target.label }) : t('assistant.shownPage', { screen: target.label })));
            }}
          >
            <Icon name="arrow" />
            {answer.showMeText ?? t('assistant.showMe')}
          </Button>
        </div>
      ) : null}
      {answer.links.length ? (
        <div className="assistant-links">
          <span className="eyebrow">{t('assistant.openScreen')}</span>
          <div className="chips">
            {answer.links.map((l) => (
              <Button
                key={l.label}
                size="sm"
                variant="secondary"
                data-testid="assistant-link"
                onClick={() => {
                  onNavigate?.();
                  navigate(l.route);
                }}
              >
                <Icon name="arrow" />
                {l.label}
              </Button>
            ))}
          </div>
        </div>
      ) : null}
      {answer.sources.length ? <p className="help">{t('assistant.basedOn', { sources: answer.sources.join(' · ') })}</p> : null}
      {answer.offline ? <p className="help">{t('assistant.offlineNote')}</p> : null}
      <VersionAndThumbs answer={answer} question={question} />
    </div>
  );
}

/** 1.8.0: one answer of any kind — a question back, steps, "what can I do here", "not today", or the guides' words. */
function TurnAnswer({ answer, question, onPress, onNavigate, live, testId }: { answer: AssistantAnswer; question: string; onPress: (p: Press) => void; onNavigate: () => void; live: boolean; testId: string }) {
  if (!answer.kind || answer.kind === 'answer') return <AnswerView answer={answer} onNavigate={onNavigate} testId={testId} question={question} />;
  // 1.8.4: a program's agreement and what is happening today read like an answer (words, a list, Show me); "each
  // program" ends with "Which program?" and its buttons
  if (answer.kind === 'program' || answer.kind === 'today')
    return (
      <div data-kind={answer.kind}>
        <AnswerView answer={answer} onNavigate={onNavigate} testId={testId} question={question} />
        {answer.clarify ? <ChipsView clarify={answer.clarify} onPress={onPress} live={live} /> : null}
      </div>
    );
  return (
    <div className="assistant-answer" data-testid={testId} data-kind={answer.kind}>
      <KindView answer={answer} onPress={onPress} onNavigate={onNavigate} live={live} />
      {answer.kind !== 'clarify' ? <VersionAndThumbs answer={answer} question={question} /> : null}
    </div>
  );
}

function errorWords(e: unknown, fallback: string): string {
  return e instanceof ApiRequestError ? (e.body?.message ?? fallback) : fallback;
}

/** One turn of the conversation: my question, then the assistant's answer or, in words, why there is none. */
interface Turn {
  id: number;
  question: string;
  /** What was typed or pressed, for the thumbs (a chip's words, or the typed question). */
  asked: string;
  answer?: AssistantAnswer;
  error?: string;
}

/** The oldest turns drop off the top after this many (the sheet is a helper, not an archive). */
const MAX_TURNS = 20;
/** Where the page behind must be pinned (useScrollLock): a phone-sized screen, or any touch screen (an iPad). */
const TOUCH_OR_NARROW = '(max-width: 900px), (pointer: coarse)';
const COARSE = '(pointer: coarse)';
/** An iPhone's keyboard is up about this long after the box takes the focus. */
const KEYBOARD_UP_MS = 350;
const matches = (query: string) => typeof window !== 'undefined' && !!window.matchMedia?.(query).matches;

/**
 * 1.5.17 §4b: the Ask sheet is a conversation — header · the note · the transcript (the only thing that
 * scrolls) · the question box and Send pinned at the bottom. On a phone it is a bottom sheet that sits on top
 * of the keyboard (useVisualViewport + src/styles/ask-sheet.css) and the page behind is pinned (useScrollLock).
 * Send clears the box, shows the question as the person's own bubble, then "Thinking…", then the answer; the
 * box keeps focus. The transcript lives as long as the app is open (closing the sheet does not clear it).
 */
export function AskPanel({ open, onOpenChange, returnFocusTo }: { open: boolean; onOpenChange: (o: boolean) => void; returnFocusTo: React.RefObject<HTMLElement | null> }) {
  const { t, i18n } = useTranslation();
  const announce = useAnnounce();
  const box = useRef<HTMLTextAreaElement>(null);
  const composer = useRef<HTMLFormElement>(null);
  const log = useRef<HTMLDivElement>(null);
  const newest = useRef<HTMLDivElement>(null);
  const nextId = useRef(1);
  const nudge = useRef(0);
  const [sheet, setSheet] = useState<HTMLDivElement | null>(null);
  const [q, setQ] = useState('');
  const [busy, setBusy] = useState(false);
  const [turns, setTurns] = useState<Turn[]>([]);
  const lang = i18n.language === 'es' ? 'es' : 'en';
  // on a phone Enter is a new line and the Send button sends; on a keyboard Enter sends
  const [phoneKeys] = useState(() => matches(COARSE));

  /**
   * 1.5.19 §4: the sheet is laid out above the keyboard (`--kb-inset`), so the box is in view by construction.
   * One nudge is kept for the case it is not: on the box's own focus (never on a viewport event - on an iPhone
   * `scrollIntoView` moves the visual viewport, whose `scroll` event would call this again, for ever), once the
   * keyboard has had time to come up, and only if the box really is outside what can be seen. Then nothing until
   * the next focus.
   */
  const nudgeOnFocus = useCallback(() => {
    window.clearTimeout(nudge.current);
    nudge.current = window.setTimeout(() => {
      const form = composer.current;
      const vv = window.visualViewport;
      if (!form || !vv || document.activeElement !== box.current) return;
      const r = form.getBoundingClientRect();
      if (r.top < vv.offsetTop || r.bottom > vv.offsetTop + vv.height) form.scrollIntoView?.({ block: 'nearest' });
    }, KEYBOARD_UP_MS);
  }, []);
  useEffect(() => () => window.clearTimeout(nudge.current), []);

  useVisualViewport(sheet);
  useScrollLock(open && matches(TOUCH_OR_NARROW));

  // the conversation follows the newest turn: its question at the top, the answer under it
  const last = turns[turns.length - 1];
  useEffect(() => {
    if (log.current && newest.current) log.current.scrollTop = Math.max(0, newest.current.offsetTop - 12);
  }, [last?.id, last?.answer, last?.error, sheet]);

  const settle = (id: number, result: Pick<Turn, 'answer' | 'error'>) => setTurns((all) => all.map((turn) => (turn.id === id ? { ...turn, ...result } : turn)));

  // 1.8.0: "Something else" under a question: the next typed words carry that conversation on
  const [carry, setCarry] = useState<AssistantThread | null>(null);

  const send = async (shown: string, question: string, extra: AskExtra = {}) => {
    if (busy) return;
    const id = nextId.current++;
    setTurns((all) => [...all, { id, question: shown, asked: question || shown }].slice(-MAX_TURNS));
    setBusy(true);
    announce(t('assistant.thinking'));
    try {
      const answer = await askAssistant(question, lang, extra);
      settle(id, { answer });
    } catch (err) {
      settle(id, { error: errorWords(err, t('assistant.failed')) });
    } finally {
      setBusy(false);
      box.current?.focus();
    }
  };

  const submit = async (e?: FormEvent) => {
    e?.preventDefault();
    const question = q.trim();
    if (question.length < 2 || busy) return;
    setQ('');
    box.current?.focus();
    const thread = carry;
    setCarry(null);
    await send(question, question, thread ? { thread } : {});
  };

  const press = (p: Press) => {
    if (p.kind === 'else') {
      setCarry(p.thread);
      box.current?.focus();
      announce(t('assistant180.typeIt'));
      return;
    }
    setCarry(null);
    if (p.kind === 'pick') void send(p.label, '', { pick: p.id, thread: p.thread });
    else void send(p.label, '', { thread: p.thread });
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        ref={setSheet}
        className="ask-sheet"
        title={t('assistant.title')}
        returnFocusTo={returnFocusTo}
        data-testid="assistant-panel"
        onOpenAutoFocus={(e) => {
          e.preventDefault();
          box.current?.focus();
        }}
        footer={
          <form ref={composer} onSubmit={(e) => void submit(e)} className="field ask-composer">
            <label htmlFor="assistant-q">{t('assistant.question')}</label>
            <div className="ask-row">
              <textarea
                id="assistant-q"
                ref={box}
                className="input"
                rows={2}
                maxLength={ASSISTANT_MAX_QUESTION}
                value={q}
                enterKeyHint={phoneKeys ? 'enter' : 'send'}
                onChange={(e) => setQ(e.target.value)}
                onFocus={nudgeOnFocus}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey && !phoneKeys && !e.nativeEvent.isComposing) {
                    e.preventDefault();
                    void submit();
                  }
                }}
                aria-describedby="assistant-q-h"
                data-testid="assistant-question"
              />
              {/* the press must not take the cursor out of the box: on a phone that would drop the keyboard */}
              <Button type="submit" busy={busy} disabled={busy || q.trim().length < 2} onMouseDown={(e) => e.preventDefault()} data-testid="assistant-ask">
                <Icon name="sparkle" />
                {busy ? t('assistant.thinking') : t('ask17.send')}
              </Button>
            </div>
            <span className="help" id="assistant-q-h">
              {t(phoneKeys ? 'ask17.helpPhone' : 'ask17.helpKeyboard')}
            </span>
          </form>
        }
      >
        <p className="help assistant-note">
          <Icon name="info" /> {t('assistant.cannotAct')}
        </p>
        {/* a log: a screen reader reads what is added (a question, an answer), never the whole conversation again */}
        <div ref={log} className="ask-log" role="log" aria-live="polite" aria-relevant="additions" aria-busy={busy} aria-label={t('ask17.conversation')} tabIndex={0} data-testid="assistant-live">
          <p className="help">{t('assistant.lead')}</p>
          {/* 1.8.0 (D-1.8.0-3): the box opens with two chips, on every screen */}
          <div className="chips ask-openers" role="group" aria-label={t('assistant180.openers')}>
            <Button size="sm" variant="secondary" disabled={busy} onClick={() => send(t('assistant180.here'), '', { mode: 'here' })} data-testid="assistant-here-chip">
              <Icon name="info" />
              {t('assistant180.here')}
            </Button>
            <Button size="sm" variant="secondary" disabled={busy} onClick={() => send(t('assistant180.walk'), '', { mode: 'walk' })} data-testid="assistant-walk-chip">
              <Icon name="arrow" />
              {t('assistant180.walk')}
            </Button>
          </div>
          <div className="ask-turns" data-testid="assistant-transcript">
            {turns.map((turn, i) => {
              const isNewest = i === turns.length - 1;
              return (
                <div key={turn.id} className="ask-turn" ref={isNewest ? newest : undefined}>
                  <p className="ask-me" data-testid="assistant-me">
                    <span className="sr-only">{t('ask17.you')} </span>
                    {turn.question}
                  </p>
                  {turn.answer ? (
                    <div className="ask-bot">
                      <span className="sr-only">{t('ask17.assistant')} </span>
                      <TurnAnswer answer={turn.answer} question={turn.asked} onPress={press} onNavigate={() => onOpenChange(false)} live={isNewest && !busy} testId={isNewest ? 'assistant-answer' : 'assistant-answer-old'} />
                    </div>
                  ) : turn.error ? (
                    <div className="ask-bot">
                      <span className="sr-only">{t('ask17.assistant')} </span>
                      <div className="warn take">
                        <Icon name="alert" />
                        <div>{turn.error}</div>
                      </div>
                    </div>
                  ) : (
                    // "Thinking…" is said through the announcer (as before); here it is for the eyes only
                    <p className="ask-bot ask-thinking" aria-hidden="true" data-testid="assistant-thinking">
                      {t('assistant.thinking')}
                    </p>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}

/** The Ask button (25 Oct 2026: in the header, beside the gear — no longer floating) and its `?` shortcut (outside a text field). */
export function AskButton() {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const btn = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    const onKey = (e: globalThis.KeyboardEvent) => {
      if (e.key !== '?' || e.ctrlKey || e.metaKey || e.altKey) return;
      const el = e.target as HTMLElement | null;
      if (el && (el.isContentEditable || /^(INPUT|TEXTAREA|SELECT)$/.test(el.tagName))) return;
      if (document.querySelector('[role="dialog"]')) return;
      e.preventDefault();
      setOpen(true);
    };
    const onAsk = () => setOpen(true);
    document.addEventListener('keydown', onKey);
    window.addEventListener(OPEN_ASK_EVENT, onAsk);
    return () => {
      document.removeEventListener('keydown', onKey);
      window.removeEventListener(OPEN_ASK_EVENT, onAsk);
    };
  }, []);
  return (
    <>
      <button ref={btn} type="button" className="ask-hdr" onClick={() => setOpen(true)} aria-haspopup="dialog" aria-keyshortcuts="?" data-testid="ask-button">
        <Icon name="sparkle" />
        <span>{t('assistant.button')}</span>
      </button>
      <AskPanel open={open} onOpenChange={setOpen} returnFocusTo={btn} />
    </>
  );
}

/**
 * Admin "Explain" (Health checks, review rows): sends the item — redacted on the server as the 0.8.0
 * diagnostic is — with "what does this mean and what should I do?". The answer sits under the item;
 * Copy diagnostic stays the path for fixes.
 */
export function ExplainButton({ req, label, testId }: { req: AssistantExplainRequest; label: string; testId?: string }) {
  const { t } = useTranslation();
  const [busy, setBusy] = useState(false);
  const [answer, setAnswer] = useState<AssistantAnswer | null>(null);
  const [error, setError] = useState<string | null>(null);
  const go = async () => {
    setBusy(true);
    setError(null);
    try {
      setAnswer(await explainWithAssistant(req));
    } catch (err) {
      setError(errorWords(err, t('assistant.failed')));
    } finally {
      setBusy(false);
    }
  };
  return (
    <>
      <Button size="sm" variant="secondary" onClick={() => go()} disabled={busy} aria-label={label} data-testid={testId}>
        <Icon name="sparkle" />
        {busy ? t('assistant.thinking') : t('assistant.explain')}
      </Button>
      <div className="explain-out" aria-live="polite" aria-atomic="true">
        {error ? (
          <div className="warn take" role="alert">
            <Icon name="alert" />
            <div>{error}</div>
          </div>
        ) : answer ? (
          <AnswerView answer={answer} testId={testId ? `${testId}-answer` : undefined} />
        ) : null}
      </div>
    </>
  );
}
